/**
 * 草稿历史：ESC 短按回退一点 / 长按清空全部草稿点
 */
import { useCallback, useEffect, useRef } from 'react';
import { message } from '../utils/antdMessage';
import {
  DRAFT_TOOLS,
  ESC_LONG_PRESS_MS,
  formatDistance,
  pathLengthMeters
} from '../utils/mapDrawingBoard';

export default function useMapDrawingHistory({
  tool,
  pushOpen,
  getMap,
  syncPreview,
  clearPreview,
  toolRef,
  draftRef,
  setDraftPoints,
  setStatusText
}) {
  const escHoldTimerRef = useRef(null);
  const escLongFiredRef = useRef(false);

  /** ESC 短按：回退最后一个草稿点 */
  const popDraftPoint = useCallback(() => {
    const map = getMap?.();
    const currentTool = toolRef.current;
    if (!currentTool || !DRAFT_TOOLS.includes(currentTool)) return;
    const pts = draftRef.current;
    if (pts.length === 0) {
      setStatusText('当前没有可清除的点（ESC 退一点 / 长按清空）');
      return;
    }
    const next = pts.slice(0, -1);
    setDraftPoints(next);
    draftRef.current = next;
    if (map) syncPreview(map, next, currentTool);
    if (next.length === 0) {
      setStatusText('已清除最后一个点');
    } else if (currentTool === 'measure' && next.length >= 2) {
      setStatusText(`已回退 1 点，当前 ${formatDistance(pathLengthMeters(next))}`);
    } else {
      setStatusText(`已回退 1 点，剩余 ${next.length} 点`);
    }
  }, [getMap, syncPreview, toolRef, draftRef, setDraftPoints, setStatusText]);

  /** ESC 长按：清空当前全部线段/区域草稿 */
  const clearAllDraftPoints = useCallback(() => {
    const currentTool = toolRef.current;
    if (!currentTool || !DRAFT_TOOLS.includes(currentTool)) return;
    if (draftRef.current.length === 0) {
      setStatusText('当前没有可清除的线段');
      return;
    }
    clearPreview();
    setStatusText('已清除全部绘制点');
    message.info('已清空当前绘制');
  }, [clearPreview, toolRef, draftRef, setStatusText]);

  // 画线/区域：ESC 退一点；长按 ESC 清空全部草稿
  useEffect(() => {
    if (!DRAFT_TOOLS.includes(tool) || pushOpen) return undefined;

    const clearEscTimer = () => {
      if (escHoldTimerRef.current) {
        clearTimeout(escHoldTimerRef.current);
        escHoldTimerRef.current = null;
      }
    };

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (e.repeat) return;
      e.preventDefault();
      escLongFiredRef.current = false;
      clearEscTimer();
      escHoldTimerRef.current = setTimeout(() => {
        escLongFiredRef.current = true;
        escHoldTimerRef.current = null;
        clearAllDraftPoints();
      }, ESC_LONG_PRESS_MS);
    };

    const onKeyUp = (e) => {
      if (e.key !== 'Escape') return;
      const wasHolding = !!escHoldTimerRef.current || escLongFiredRef.current;
      clearEscTimer();
      if (!wasHolding) return;
      if (escLongFiredRef.current) {
        escLongFiredRef.current = false;
        return;
      }
      popDraftPoint();
    };

    const onBlur = () => {
      clearEscTimer();
      escLongFiredRef.current = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      clearEscTimer();
      escLongFiredRef.current = false;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [tool, pushOpen, popDraftPoint, clearAllDraftPoints]);

  return { popDraftPoint, clearAllDraftPoints };
}
